import { initReactI18next } from "react-i18next"

import {
  type SelectableLanguageCode,
  type SupportedLanguageCode,
  type TranslationNamespace,
  selectableLanguageCodes,
  supportedLanguageCodes,
} from "@/models/i18n"
import { translationNamespaces } from "@/models/i18n"
import i18next from "i18next"
import type { InitOptions } from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import type { DetectorOptions } from "i18next-browser-languagedetector"
import ChainedBackend from "i18next-chained-backend"
import HttpBackend from "i18next-http-backend"

import { LANGUAGE_LOCAL_STORAGE_KEY } from "./constants"

// Map short language codes to their full regional variants
const languageToLocaleMap: Record<string, string> = {
  en: "en-US",
  ja: "ja-JP",
  th: "th-TH",
}

/**
 * Maps a short language code to its full locale code.
 * E.g., "en" → "en-US", "ja" → "ja-JP", "th" → "th-TH"
 * If the language is already a full locale code, returns it as-is.
 */
export const getLocaleCode = (language = "en"): string => {
  return languageToLocaleMap[language] || language
}

// Map full regional variants to short language codes
const localeToLanguageMap: Record<string, string> = {
  "ja-JP": "ja",
  "th-TH": "th",
}

/**
 * Maps a full regional variant to its short language code.
 * E.g., "ja-JP" → "ja", "th-TH" → "th"
 * If the locale is already a short language code, returns it as-is.
 */
export const getLanguageCode = (locale = "en-US"): string => {
  return localeToLanguageMap[locale] || locale
}

/**
 * Maps an i18n resolved language to a selectable language-switcher value.
 * E.g., "ja-JP" → "ja", "en" → "en-US"
 */
export const getSelectableLanguageCode = (
  language = "en-US"
): SelectableLanguageCode => {
  if (selectableLanguageCodes.includes(language as SelectableLanguageCode)) {
    return language as SelectableLanguageCode
  }

  const regionalParent = getLanguageCode(language)
  if (
    selectableLanguageCodes.includes(regionalParent as SelectableLanguageCode)
  ) {
    return regionalParent as SelectableLanguageCode
  }

  const localeVariant = getLocaleCode(language)
  if (
    selectableLanguageCodes.includes(localeVariant as SelectableLanguageCode)
  ) {
    return localeVariant as SelectableLanguageCode
  }

  return "en-US"
}

/**
 * Maps an i18n language code to the corresponding MUI locale code.
 * MUI locale strings use the format "languageREGION" (e.g., "enUS", "jaJP").
 * This handles short language codes (e.g., "ja") by mapping them to their
 * full regional variants.
 */
export const getMuiLocaleCode = (language = "en-US"): string => {
  return getLocaleCode(language).replace("-", "")
}

const formatter: Record<string, (value: number | string) => string> = {
  capitalizeFirst: (value: number | string): string => {
    if (typeof value !== "string") {
      return String(value ?? "")
    }

    return value.length > 0
      ? value.charAt(0).toUpperCase() + value.slice(1)
      : value
  },
}

// const LOCALIZATION_CDN_KEY: string =
//   process.env.REACT_APP_LOCALIZATION_CDN_KEY ?? ""
// const LOCALIZATION_CDN_URL_ORIGIN = "https://distributions.crowdin.net"
// const LOCALIZATION_CDN_URL_PATH: string =
// process.env.REACT_APP_LOCALIZATION_CDN_URL_PATH ?? ""

const timestamp = Date.now()

export const detectionOptions: DetectorOptions = {
  caches: ["localStorage"],
  lookupLocalStorage: LANGUAGE_LOCAL_STORAGE_KEY,
  order: ["localStorage", "navigator", "htmlTag"],
}

export const defaultInitOptions: InitOptions = {
  defaultNS: "common" satisfies TranslationNamespace,
  detection: detectionOptions,
  fallbackLng: "en" satisfies SupportedLanguageCode, // "en" resources should only exist in the codebase (not the l10n CDN) and serve as the "source" resources for the cloud l10n service
  fallbackNS: "common" satisfies TranslationNamespace,
  interpolation: {
    escapeValue: false,
  },
  load: "all",
  ns: [...translationNamespaces] satisfies TranslationNamespace[],
  supportedLngs: supportedLanguageCodes,
}

// const getLocalizationManifest = async (): Promise<Manifest> => {
//   // Ideally we would use @crowdin/ota-client, but module importing/exporting isn't working in TypeScript
//   // e.g. const crowdinHttpClient = new OtaClient(LOCALIZATION_CDN_KEY)
//   const response = await httpClient.get(
//     `${LOCALIZATION_CDN_URL_ORIGIN}/${LOCALIZATION_CDN_KEY}/manifest.json`
//   )

//   // Mass eslint disable @typescript-eslint/no-unsafe-return

//   return response.data
// }

// CrowdIn CDN init path is commented out below until it's turned back on.
// Initialize i18n with local language support only.
const initOptions: InitOptions = {
  ...defaultInitOptions,
  backend: {
    backends: [HttpBackend],
    backendOptions: [
      { loadPath: `/locales/{{lng}}/{{ns}}.json?timestamp=${timestamp}` },
    ],
  },
}

// eslint-disable-next-line import/no-named-as-default-member
void i18next
  .use(ChainedBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(initOptions)

for (const key in formatter) {
  i18next.services.formatter?.add(key, formatter[key])
}

// CDN init (disabled):
// throw new Error("CrowdIn CDN has been intentionally turned off")
//
// Initialize i18n with CDN language support and local fallback
// const manifest: Manifest = await getLocalizationManifest()
// const { languages, languageMapping } = manifest
// // migration to strict mode batch disable
//
// const localeCodes: string[] = languages.map((language) => {
//   const hasRegion = !!new Intl.Locale(language).region
//   // Mass lint disable
//   // Mass eslint disable @typescript-eslint/no-unsafe-return
//
//   return hasRegion
//     ? language
//     : Array.isArray(languageMapping)
//       ? ""
//       : languageMapping[language].locale
// })
//
// const initOptions: InitOptions = {
//   ...defaultInitOptions,
//   backend: {
//     backends: [HttpBackend, HttpBackend],
//     backendOptions: [
//       {
//         loadPath: `${LOCALIZATION_CDN_URL_ORIGIN}/${LOCALIZATION_CDN_KEY}/content${LOCALIZATION_CDN_URL_PATH}/{{lng}}/{{ns}}.json?timestamp=${manifest.timestamp}`,
//       },
//       {
//         loadPath: `/locales/{{lng}}/{{ns}}.json?timestamp=${timestamp}`,
//       },
//     ],
//   },
//   supportedLngs: ["en", ...localeCodes],
// }
//
// i18next
//   .use(ChainedBackend)
//   .use(LanguageDetector)
//   .use(initReactI18next)
//   .init(initOptions)
//
// for (const key in formatter) {
//   i18next.services.formatter?.add(key, formatter[key])
// }

export default i18next
